import ToggleInput from "@components/inputs/ToggleInput";
import { primeServices } from "@services/apis/Prime";
import { UpiStatusItem } from "@services/models/Prime/UpiStatusModel";
import webSocketService from "@services/websocket/WebsocketServices";
import { useCallback, useEffect, useRef, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

const UPI_SERVICE_UPDATE_EVENT = "admin_upi_service_update";

const formatServiceShortLabel = (service: string) => {
  if (service === "cashIn") return "In";
  if (service === "cashOut") return "Out";
  return service;
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const err = error as {
    response?: { data?: { msg?: string } };
    message?: string;
  };
  return err?.response?.data?.msg || err?.message || fallback;
};

const InUpiServiceStatus = () => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;

  const [services, setServices] = useState<UpiStatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);

  const handleServiceStatusUpdate = useCallback((payload: UpiStatusItem) => {
    if (!payload?.providerId || !payload?.service) return;

    setServices((prev) => {
      const exists = prev.some(
        (entry) =>
          entry.providerId === payload.providerId &&
          entry.service === payload.service,
      );

      if (!exists) {
        return [...prev, payload];
      }

      return prev.map((entry) =>
        entry.providerId === payload.providerId &&
        entry.service === payload.service
          ? {
              ...entry,
              status: payload.status,
              active: payload.active,
            }
          : entry,
      );
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadStatuses = async () => {
      try {
        setLoading(true);
        const response = await primeServices.getUpiStatus();
        if (cancelled) return;
        setServices(response.data || []);
      } catch (error) {
        if (!cancelled) {
          handleErrorRef.current({
            message: getApiErrorMessage(
              error,
              "Failed to fetch UPI service status",
            ),
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStatuses();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      webSocketService.protectedSocket?.off(UPI_SERVICE_UPDATE_EVENT);
      webSocketService.onMessage(
        UPI_SERVICE_UPDATE_EVENT,
        true,
        handleServiceStatusUpdate,
      );
    } catch (error) {
      console.error("WebSocket initialization error:", error);
    }

    return () => {
      webSocketService.offMessage(
        UPI_SERVICE_UPDATE_EVENT,
        true,
        handleServiceStatusUpdate,
      );
    };
  }, [handleServiceStatusUpdate]);

  const handleToggle = async (item: UpiStatusItem) => {
    const key = `${item.providerId}-${item.service}`;
    const nextStatus = item.active ? "disable" : "enable";

    setUpdatingKey(key);
    try {
      const response = await primeServices.updateUpiStatus({
        service: item.service,
        status: nextStatus,
      });
      handleSuccess({
        message: response.msg || "UPI service status updated successfully",
      });
      setServices((prev) =>
        prev.map((entry) =>
          entry.providerId === item.providerId && entry.service === item.service
            ? response.data
            : entry,
        ),
      );
    } catch (err: any) {
      handleError({
        message:
          err?.response?.data?.msg || "Failed to update UPI service status",
      });
    } finally {
      setUpdatingKey(null);
    }
  };

  if (loading) {
    return (
      <span className="text-text-subtle text-[11px] whitespace-nowrap">
        Loading UPI...
      </span>
    );
  }

  if (services.length === 0) {
    return null;
  }

  const providers = [...new Set(services.map((item) => item.providerId))];

  return (
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-text-subtle text-[11px] font-semibold whitespace-nowrap">
        UPI Maint.
      </span>
      {providers.map((providerId) => (
        <div
          key={providerId}
          className="flex items-center gap-1.5 border border-border-subtle rounded-md px-2 py-1 bg-bg-page-body"
        >
          <span className="text-text-base text-[11px] font-semibold uppercase">
            {providerId}
          </span>
          {services
            .filter((item) => item.providerId === providerId)
            .map((item) => {
              const key = `${item.providerId}-${item.service}`;
              const isUpdating = updatingKey === key;
              return (
                <div key={key} className="flex items-center gap-1">
                  <span
                    className={`text-[10px] font-medium ${
                      item.active
                        ? "text-text-status-green"
                        : "text-text-status-red"
                    }`}
                  >
                    {formatServiceShortLabel(item.service)}
                  </span>
                  <div
                    className={
                      isUpdating ? "opacity-50 pointer-events-none" : ""
                    }
                  >
                    <ToggleInput
                      isToggled={item.active}
                      onToggle={() => handleToggle(item)}
                      size="sm"
                    />
                  </div>
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
};

export default InUpiServiceStatus;
